import { PATHS } from '@src/consts'
import type { ChallengeScope } from '@src/consts'

import { challengeToken } from '../challenge-token'

/**
 * Returns true if `value` is an object (non-null, non-array) — used to safely
 * narrow `unknown` properties without `as` casts.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Normalise the two error shapes that can arrive in onError callbacks:
 *
 *   1. Real shape (use-axios interceptor rejects with err.response):
 *      { status: number, data: object, ... }   ← status/data at top level
 *
 *   2. Raw AxiosError / other paths:
 *      { response: { status: number, data: object } }   ← nested
 *
 * Returns the { status, data } pair if either shape matches, otherwise null.
 */
export function pickResponseLike(
  error: unknown
): { status: number; data: Record<string, unknown> } | null {
  if (!isPlainObject(error)) {
    return null
  }

  // Shape 1: status and data live at the top level (use-axios actual reject shape)
  if (
    'status' in error &&
    typeof error.status === 'number' &&
    'data' in error &&
    isPlainObject(error.data)
  ) {
    return { status: error.status, data: error.data }
  }

  // Shape 2: nested inside error.response (raw AxiosError / other paths)
  if ('response' in error && isPlainObject(error.response)) {
    const response = error.response
    if (
      'status' in response &&
      typeof response.status === 'number' &&
      'data' in response &&
      isPlainObject(response.data)
    ) {
      return { status: response.status, data: response.data }
    }
  }

  return null
}

/**
 * Side-effect-free predicate: returns true when the thrown value represents a
 * challenge-related failure that requires re-authentication:
 *   - HTTP 403 + body.code === 'challenge-required'   (token missing / expired)
 *   - HTTP 400 + body.code === 'invalid-challenge-token'  (token tampered)
 *
 * Use this in feature-layer catch blocks to bail out early before showing an
 * error snackbar, so that the UI does not flash a generic error message while
 * the challenge-recovery redirect is already in progress.
 */
export function isChallengeRecoveryError(error: unknown): boolean {
  const responseLike = pickResponseLike(error)
  if (responseLike === null) {
    return false
  }

  const { status, data } = responseLike
  const code = 'code' in data && typeof data.code === 'string' ? data.code : ''

  return (
    (status === 403 && code === 'challenge-required') ||
    (status === 400 && code === 'invalid-challenge-token')
  )
}

/**
 * Inspect an unknown thrown value and, when it represents a challenge-related
 * failure, perform the recovery side effects:
 *   1. Remove the stored token for `scope`.
 *   2. Redirect the user to the challenge page (window.location.replace so the
 *      browser history entry is replaced, not pushed).
 *
 * Returns true when it handled the error, false otherwise so that existing
 * onError handlers can continue their own processing.
 *
 * @param redirectPath - Override the post-challenge return destination.
 *   When omitted the current pathname + search + hash is used so the user
 *   lands back on the page that triggered the error.  Pass an explicit path
 *   (e.g. PATHS.USERS) when the SSOT specifies a different return target
 *   (e.g. bulk-invite must return to the user list, not the invite page).
 */
export function handleChallengeError(
  error: unknown,
  scope: ChallengeScope,
  redirectPath?: string
): boolean {
  if (!isChallengeRecoveryError(error)) {
    return false
  }

  const destination =
    redirectPath ??
    window.location.pathname + window.location.search + window.location.hash

  challengeToken.remove(scope)
  window.location.replace(
    PATHS.CHALLENGE +
      '?scope=' +
      scope +
      '&redirect_url=' +
      encodeURIComponent(destination)
  )
  return true
}
